/**
 *
 * IntlContext
 *
 */

import React, { createContext, useState, useEffect, useMemo } from 'react';
import store from 'store';
import { IntlProvider as ReactIntlProvider } from 'react-intl';
import { log } from 'lib/log';
import { Messages } from './messages';

// locale
import 'intl-pluralrules';
import '@formatjs/intl-relativetimeformat/polyfill';
import '@formatjs/intl-relativetimeformat/dist/locale-data/en';
import '@formatjs/intl-relativetimeformat/dist/locale-data/de';

export type Locale = 'en' | 'de';

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en',
  setLocale: () => {},
});

export interface IntlProviderProps {
  /** If not provided, `en` will be used. */
  defaultLocale?: Locale;
  messages: Messages;
}

export const LOCALE_KEY = 'locale';

export const IntlProvider: React.FC<IntlProviderProps> = ({
  defaultLocale,
  messages,
  children,
}) => {
  const [locale, setLocale] = useState<Locale>(store.get(LOCALE_KEY) || defaultLocale || 'en');

  useEffect(() => {
    store.set(LOCALE_KEY, locale);
  }, [locale]);

  const mappedMessages = useMemo(
    () => ({
      en: Object.keys(messages).reduce((acc, curr) => ({ ...acc, [curr]: messages[curr].en }), {}),
      de: Object.keys(messages).reduce((acc, curr) => {
        const deMessage = messages[curr].de;
        if (!deMessage && locale === 'de') {
          log.warn(
            `intl: key "${curr}" does not have a "de" message, using "en" message sa fallback`,
          );
        }
        return {
          ...acc,
          [curr]: deMessage ? deMessage : messages[curr].en,
        };
      }, {}),
    }),
    [messages, locale],
  );

  return (
    <ReactIntlProvider locale={locale} messages={mappedMessages[locale]}>
      <LocaleContext.Provider value={{ locale, setLocale }}>{children}</LocaleContext.Provider>
    </ReactIntlProvider>
  );
};
